import { Controller } from "@hotwired/stimulus"

// Listens for `logs:filter-status` custom events dispatched by the page-level
// query-composer controller. Renders a transient labeled pill in the drawer
// footer with a fade-in/fade-out, then auto-clears.
//
// Lives in the drawer footer rather than the composer's element because the
// drawer mounts in #hotwire-modal — a sibling of the composer root, not a
// descendant — so Stimulus targets can't bridge the two trees. Custom events
// on `document` are the standard cross-tree handoff in Hotwire.

// Tailwind classes (not Bootstrap label-*) because the drawer footer is inside
// UI::Drawer's `.tw` scope, where Bootstrap rules `:not(:where(.tw *, .tw))`
// don't match.
const COLOR_CLASSES_BY_TYPE = {
  success: ["bg-green-100", "text-green-800", "dark:bg-green-900", "dark:text-green-100"],
  error:   ["bg-red-100", "text-red-800", "dark:bg-red-900", "dark:text-red-100"],
  info:    ["bg-blue-100", "text-blue-800", "dark:bg-blue-900", "dark:text-blue-100"],
}
const DEFAULT_COLOR = ["bg-gray-100", "text-gray-800", "dark:bg-gray-700", "dark:text-gray-100"]
const ALL_COLOR_CLASSES = [...new Set([...Object.values(COLOR_CLASSES_BY_TYPE).flat(), ...DEFAULT_COLOR])]

const HOLD_MS_BY_TYPE = {
  success: 2000,
  info: 2500,
  error: 4000,
}

export default class extends Controller {
  static targets = ["message"]

  disconnect() {
    clearTimeout(this._timeout)
  }

  // Wired via data-action="logs:filter-status@document->logs--filter-status#show"
  // on the controller's host element.
  show(event) {
    const { type, message } = event.detail || {}
    if (!this.hasMessageTarget || !message) return

    clearTimeout(this._timeout)

    const target = this.messageTarget
    const colorClasses = COLOR_CLASSES_BY_TYPE[type] || DEFAULT_COLOR
    const holdMs = HOLD_MS_BY_TYPE[type] || HOLD_MS_BY_TYPE.error

    target.textContent = message
    target.classList.remove(...ALL_COLOR_CLASSES)
    target.classList.add(...colorClasses)
    target.classList.remove("opacity-0")
    target.classList.add("opacity-100")

    this._timeout = setTimeout(() => {
      target.classList.remove("opacity-100")
      target.classList.add("opacity-0")
    }, holdMs)
  }
}
